import React, { useEffect, useState } from 'react';
import './Notifications.scss';
import { getNotification, updateUnseenNotification } from '../../services/api/NotificationsApi';
import { useNavigate } from 'react-router-dom';
import { useBubbleSettings } from '../../context/BubbleSettingsContext';


export const Notifications = ({ show }) => {
  const [notifications, setNotifications] = useState([]);
  const [notifyCount, setNotifyCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [markingAllRead, setMarkingAllRead] = useState(false);


  const navigate = useNavigate();
  const { fetchAndUpdateBubbleSettings } = useBubbleSettings();


  const handleNotificationClick = (notif) => {
    // 1. Navigate IMMEDIATELY (Fire and Forget)
    try {
      switch (notif.module) {
        case 'Task':
          navigate(`/home/tasks/viewtask/${notif.source}`);
          break;
        case 'Document':
          navigate(`/home/cases/casedetails/${notif.courtRef}/additonaldoc/${notif.source}`);
          break;
        case 'Case':
          navigate(`/home/cases/${notif.source}`);
          break;
        case 'Case-Assignment':
          navigate(`/home/cases/casedetails/${notif.courtRef}`);
          break;
        case 'Scraper':
          if (notif.courtRef) {
             navigate(`/home/cases/casedetails/${notif.courtRef}`);
          } else {
             console.warn('Scraper notification missing courtRef');
          }
          break;
        case 'Expense':
          navigate(`/home/expenses/${notif.source}`);
          break;
        default:
          console.warn('No route defined for module:', notif.module);
          break;
      }
    } catch (e) {
      console.error("Navigation error:", e);
    }

    // 2. Perform updates in Background (Do NOT await)
    updateUnseenNotification(notif._id)
      .then(() => fetchAndUpdateBubbleSettings())
      .catch((err) => console.error('Error updating notification status in background:', err));
  };


  const handleMarkAllAsRead = async () => {
    setMarkingAllRead(true);
    try {
      // Get all unread notification IDs
      const unreadNotifications = notifications.filter(notif => !notif.seen);
     
      // Mark each notification as read
      await Promise.all(
        unreadNotifications.map(notif => updateUnseenNotification(notif._id))
      );
     
      // Update the bubble settings and notification count
      await fetchAndUpdateBubbleSettings();
     
      // Update local state
      setNotifyCount(0);
      setNotifications(notifications.map(notif => ({ ...notif, seen: true })));
     
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    } finally {
      setMarkingAllRead(false);
    }
  };


  useEffect(() => {
    let intervalId;
    
    // Function now accepts a 'silent' parameter to avoid flickering the loading spinner
    const fetchNotifications = async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const res = await getNotification();
        setNotifyCount(res.data.unSeenNotifications)
        const data = res.data.result || [];
        setNotifications(data);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        if (!silent) setLoading(false);
      }
    };

    if (show) {
      // 1. Initial fetch when opened (Show loading spinner)
      fetchNotifications(false);

      // 2. Setup interval for list updates (No loading spinner)
      intervalId = setInterval(() => {
        if (document.visibilityState === 'visible') {
          fetchNotifications(true); // Pass true for silent update
        }
      }, 20000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [show]);


  if (!show) return null;


  return (
    <div className="notification-box">
      <div className="notification-header">
        <h4>Notifications ({notifyCount})</h4>
        {notifyCount > 0 && (
          <span
            className="mark-all-read-text"
            onClick={handleMarkAllAsRead}
          >
            {markingAllRead ? 'Marking...' : 'Mark all as seen'}
          </span>
        )}
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : notifications.length === 0 ? (
        <p>No notifications</p>
      ) : (
        <div className="notification-scroll-container">
          {notifications.map((notif) => (
            <div
              key={notif._id}
              className="notification-item"
              onClick={() => handleNotificationClick(notif)}
            >
              <div className='notif-per-item'>
              <span>
                {notif.action} by <strong>{notif.byUser.name}</strong> to <strong>{notif.toUser.name}</strong>
              </span>
              {notif.module === 'Scraper' && notif.courtRef && (
                <div className="court-ref"><b>CNR:</b> {notif.courtRef}</div>
              )}
              <div className="timestamp">
                {new Date(notif.createdAt).toLocaleString()}
              </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};